import * as React from 'react';
import { Card, Stack } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { PERSONAL_INSTITUTION_ID } from 'cb-unified';
import { InstitutionView, UserSimpleView } from 'cb-model';
import { useCacheRecord } from 'gossamer-front-end';
import styles from './EventInstitutionCard.module.css';
import { CreateForumConvoModal } from '../convo/CreateForumConvoModal';
import FollowInstitution from './FollowInstitution';

const DESCRIPTION_TRUNCATE_LENGTH = 100;

interface EventPersonalInstitutionCardProps {
  userId: string;
}

export const EventPersonalInstitutionCard = (props: EventPersonalInstitutionCardProps): React.JSX.Element => {
  const mainUserRecord = useCacheRecord(UserSimpleView, props.userId);
  return (
    <Card>
      <Card.Header>
        {mainUserRecord.data &&
          renderProvider(
            mainUserRecord.data.photo,
            `${mainUserRecord.data.first_name} ${mainUserRecord.data.last_name}'s Event`,
            true,
            null,
          )}
      </Card.Header>
      <Card.Body>
        <Stack direction="horizontal" gap={2} className="mb-3">
          <CreateForumConvoModal
            buttonLabel="Contact"
            forumType="User"
            forumKey={props.userId}
            institutionId={PERSONAL_INSTITUTION_ID}
          />
        </Stack>
      </Card.Body>
    </Card>
  );
};

interface EventInstitutionCardProps {
  institutionId: number;
  showAsLink?: boolean;
  showBanner?: boolean;
  showHostedBy?: boolean;
}

export const EventInstitutionCard = (props: EventInstitutionCardProps): React.JSX.Element => {
  const institutionRecord = useCacheRecord(InstitutionView, String(props.institutionId));
  const [showingMore, setShowingMore] = React.useState(false);
  const descriptionParts = !institutionRecord.data?.description
    ? ['']
    : institutionRecord.data.description.length < DESCRIPTION_TRUNCATE_LENGTH
      ? [institutionRecord.data.description]
      : [
          institutionRecord.data.description.substring(0, DESCRIPTION_TRUNCATE_LENGTH),
          institutionRecord.data.description.substring(DESCRIPTION_TRUNCATE_LENGTH),
        ];

  return (
    <Card>
      {props.showBanner && institutionRecord.data?.banner && (
        <Card.Img variant="top" src={institutionRecord.data.banner} className={styles.EventInstitutionCardImage} />
      )}
      <Card.Header className={styles.CardHeader}>
        {renderProvider(
          institutionRecord.data?.logo || '/assets/icons/building-library.svg',
          institutionRecord.data?.name || '',
          props.showHostedBy,
          props.showAsLink ? `/website/institution/${props.institutionId}` : null,
        )}
      </Card.Header>
      <Card.Body>
        <Stack direction="horizontal" gap={2} className="mb-3">
          <FollowInstitution institutionRecord={institutionRecord} />
          <CreateForumConvoModal
            buttonLabel="Contact"
            forumType="Institution"
            forumKey={String(props.institutionId)}
            institutionId={props.institutionId}
          />
        </Stack>
        <Card.Text>
          {descriptionParts[0]}
          {descriptionParts.length > 1 && !showingMore && (
            <a onClick={() => setShowingMore(true)}>
              ... <span className={styles.ShowMoreLess}>show more</span>
            </a>
          )}
          {descriptionParts.length > 1 && showingMore && (
            <span>
              {descriptionParts[1]}
              <a onClick={() => setShowingMore(false)}>
                &nbsp;&nbsp;&nbsp;<span className={styles.ShowMoreLess}>show less</span>
              </a>
            </span>
          )}
        </Card.Text>
        <div className={styles.SmallerText}>
          {institutionRecord.data?.address && <div>{institutionRecord.data.address}</div>}
          {institutionRecord.data?.contact_email && (
            <div>
              <a href={`mailto:${institutionRecord.data.contact_email}`}>{institutionRecord.data.contact_email}</a>
            </div>
          )}
          {institutionRecord.data?.contact_phone && <div>{institutionRecord.data.contact_phone}</div>}
        </div>
      </Card.Body>
    </Card>
  );
};

const renderProvider = (logo: string, name: string, showHostedBy: boolean, linkUrl: string) => (
  <div className={styles.ProviderBlock}>
    {showHostedBy && <span>Hosted by:</span>}
    <img src={logo} alt="event provider logo" />
    {!!linkUrl && <NavLink to={linkUrl}>{name}</NavLink>}
    {!linkUrl && <span>{name}</span>}
  </div>
);
